/* ============================================================================
   VoxelPals · 3D 世界「THE VITRINE」动效 — Batch 1 + 2 + 3
   规范：docs/threed-vitrine-spec.md §2.1–§2.8 / §3.5 / §7.4

   设计前提：
   - 只在 data-skin="vitrine" 的页面运行；brick 世界零开销。
   - GSAP 是本地 vendored 的；加载失败必须静默降级成"静态但完全可读"。
   - 入场前置态写在 CSS 的 html.t3-js 下（不是 JS 里 set），避免未样式化内容闪一下。
     head() 里的内联脚本负责加 t3-js 并挂 1.2s 保险丝：本文件没跑起来就自动松开。
   - 与 app.js 的 [data-reveal] 井水不犯河水：3D 新版块的元素不带 data-reveal。
   ========================================================================= */
(function () {
  "use strict";

  var doc = document.documentElement;
  if (doc.dataset.skin !== "vitrine") return;
  if (window.__vpThreedMotionController) return;
  // Content is readable immediately; motion is mounted only in the visible world.
  doc.classList.remove("t3-js");

  /* 松开 CSS 的入场前置态，让页面回到"静态可读"。 */
  function release() { doc.classList.remove("t3-js"); }

  // Product browsing is native and must keep working if the animation engine fails.
  (function mountCollectionBrowse() {
    var room = document.querySelector(".studio-room");
    if (!room || room.dataset.browseReady) return;
    var track = room.querySelector("[data-t3-track]");
    var controls = room.querySelector("[data-room-controls]");
    if (!track || !controls) return;
    var prev = controls.querySelector("[data-room-prev]");
    var next = controls.querySelector("[data-room-next]");
    if (!prev || !next) return;
    room.dataset.browseReady = "true";
    controls.hidden = false;
    // 轨道可能被 build.py 的内联脚本改造成无限循环（复制一组卡片，到边界回卷）。
    // 循环里没有「头」和「尾」：还按边界禁用按钮的话，轨道停在起始位 scrollLeft=1
    // 就会把 prev 直接禁掉，用户点不动。2026-09-08 实测过。
    function looping() { return track.dataset.loopReady === "1"; }
    function sync() {
      if (looping()) { prev.disabled = false; next.disabled = false; return; }
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = max < 2 || track.scrollLeft >= max - 2;
    }
    /* ⚠️ 不要改回 scrollTo({behavior:'smooth'})。2026-09-08 实测它在这个容器上
       会被静默取消：打桩确认 move() 以正确参数调到了原生 scrollTo、元素也在文档里，
       轨道就是不动；隔几秒手工执行同一句又是好的。嫌疑是懒加载图落地重排和
       ScrollTrigger.refresh() 撞在同一个时间窗。直接写 scrollLeft 这条路是确定的
       —— 拖拽和循环回卷一直走它，从没失手。 */
    var raf = 0;
    function animateTo(target) {
      var unit = track.scrollWidth / 2;
      // 往左翻出第一组时先整体回卷一组，动画就不会先撞到 0 再瞬移，看着不跳。
      if (looping() && target < 0 && unit > 10) {
        track.scrollLeft += unit;
        target += unit;
      }
      var start = track.scrollLeft;
      var delta = target - start;
      if (!delta) return;
      var fuse = 0;
      var done = function () {
        clearTimeout(fuse);
        delete track.dataset.animating;
        if (track.__loopWrap) track.__loopWrap();   // 动画期间回卷被挂起，收尾补一次
        sync();
      };
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        track.scrollLeft = target; done(); return;
      }
      cancelAnimationFrame(raf);
      track.dataset.animating = "1";
      var t0 = performance.now(), dur = 420;
      var step = function (now) {
        var p = Math.min(1, (now - t0) / dur);
        track.scrollLeft = start + delta * (1 - Math.pow(1 - p, 3));
        if (p < 1) raf = requestAnimationFrame(step); else done();
      };
      raf = requestAnimationFrame(step);
      /* 保险丝：rAF 在后台标签页、省电模式和无头浏览器里会被节流甚至不派发，
         动画跑不完 → dataset.animating 永远留着 → 回卷被挂起、下一次点击也失效。
         2026-09-08 实测无头环境下「点一下动、再点一下不动」就是这么来的。
         到点还没跑完就直接落位，宁可少一段缓动也不能卡住。 */
      fuse = setTimeout(function () {
        if (track.dataset.animating === "1") { cancelAnimationFrame(raf); track.scrollLeft = target; done(); }
      }, dur + 160);
    }
    function move(direction) {
      var cards = Array.prototype.slice.call(track.querySelectorAll("[data-t3-card]"));
      if (!cards.length) return;
      var origin = cards[0].offsetLeft;
      var stops = cards.map(function (card) { return card.offsetLeft - origin; });
      var current = track.scrollLeft;
      if (looping()) {
        // 循环里没有两端可以对齐，按一张卡的步长从当前位置推走，越界交给回卷。
        var pitch = stops.length > 1 ? stops[1] - stops[0] : Math.max(240, track.clientWidth * 0.8);
        animateTo(current + direction * pitch);
        return;
      }
      var target = direction > 0 ? stops.find(function (x) { return x > current + 4; }) :
        stops.reverse().find(function (x) { return x < current - 4; });
      if (target === undefined) target = direction > 0 ? track.scrollWidth : 0;
      animateTo(target);
    }
    prev.addEventListener("click", function () { move(-1); });
    next.addEventListener("click", function () { move(1); });
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync, { passive: true });
    window.addEventListener("pageshow", sync);
    if (window.ResizeObserver) new ResizeObserver(sync).observe(track);
    sync();
  })();

  // R47: user-owned comparison value. Native input supports mouse, touch and keys.
  (function mountPhotoCompare() {
    var stage = document.querySelector("[data-photo-compare]");
    if (!stage || stage.dataset.compareReady) return;
    var range = stage.querySelector("[data-photo-range]");
    if (!range) return;
    function sync() {
      var value = Number(range.value);
      value = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 50;
      stage.style.setProperty("--t3-wipe", value + "%");
      range.setAttribute("aria-valuetext", value + "% photo, " + (100 - value) + "% character");
    }
    range.addEventListener("input", sync);
    range.addEventListener("change", sync);
    window.addEventListener("pageshow", sync);
    stage.dataset.compareReady = "true";
    range.disabled = false;
    range.hidden = false;
    var instruction = document.querySelector("[data-photo-instruction]");
    if (instruction) instruction.textContent = "Drag to compare. Or use the arrow keys.";
    sync();
  })();

  if (!window.gsap || !window.ScrollTrigger) { doc.dataset.t3MotionState = "static-no-engine"; release(); return; }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);

  /* 走到这里说明 GSAP 在手，由本文件接管入场态，保险丝不再触发。 */
  var mounted = false;
  var motionContext = null;
  var mediaContext = null;
  var cleanupTasks = [];
  var lifecycleCleanup = [];
  var lifecycleTick = 0;
  var disposed = false;
  var suspended = false;
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  function dataSaving() {
    return !!(connection && (connection.saveData || /^(slow-2g|2g|3g)$/.test(connection.effectiveType || "")));
  }
  function listen(target, type, handler, options) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, handler, options);
    lifecycleCleanup.push(function () { target.removeEventListener(type, handler, options); });
  }
  function activeWorld() {
    return !disposed && !suspended && doc.dataset.world === "3d" && doc.dataset.entry !== "gateway" &&
      !doc.classList.contains("is-world-transitioning") && !document.hidden;
  }

  var $  = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  var hero = $("[data-t3-hero]");

  /* ---------------------------------------------------------- 页头实测高度 */
  /* 站内页头是静态占位的（不是 sticky），hero 用满 100dvh 会把光池/CTA/铜牌
     整体顶到折下去。CSS 里 --t3-nav-h 有兜底值，这里用实测值覆盖，
     因为页头高度会随字体加载、换行、断点变。 */
  /* ⚠️ Batch 2 修：这里原本是「nav 一 resize 就 syncNavHeight + ScrollTrigger.refresh()」。
     页头在滚过自身高度时会被 app.js 加 .is-compact，高度从 179 掉到 62 —— 也就是说
     每次进入/离开紧凑态都会触发一次 refresh。Batch 1 没有 pin，看不出问题；
     03 LIGHT ROOM 的 pin 一上来就露馅：refresh 会临时拆掉 pin 再装回去，
     正在滚动的那一下被打断，实测滚动位置停在 158~195px 不动。
     两条防线：紧凑态的高度根本不是 hero 要的参照（hero 在页顶，那时页头是展开的），
     直接不采样；值没变就不 refresh。 */
  var lastNavH = 0;
  function syncNavHeight() {
    var nav = document.getElementById("nav");
    if (!nav || nav.classList.contains("is-compact")) return false;
    var h = Math.round(nav.getBoundingClientRect().height);
    if (h <= 0 || h === lastNavH) return false;
    lastNavH = h;
    doc.style.setProperty("--t3-nav-h", h + "px");
    return true;
  }
  function watchNavHeight() {
    syncNavHeight();
    var nav = document.getElementById("nav");
    if (!nav || !window.ResizeObserver) return;
    var observer = new ResizeObserver(function () {
      if (mounted && activeWorld() && syncNavHeight()) ScrollTrigger.refresh();
    });
    observer.observe(nav);
    cleanupTasks.push(function () { observer.disconnect(); });
  }

  /* Safari 上 clip-path inset 收到 0% 时偶发闪烁，终态留 -1px。 */
  var CLIP_OPEN = "inset(0 -1px 0 0)";
  var CLIP_SHUT = "inset(0 100% 0 0)";

  /* ------------------------------------------------------------ 计数动画 */
  /* build.py 直接把真实数字写进 textContent（无 JS 时显示正确值），
     这里先归零再数上去，所以降级路径永远是对的。 */
  function counters(animate) {
    $$("[data-count-to]").forEach(function (el) {
      var to = parseFloat(el.dataset.countTo);
      if (isNaN(to)) return;
      if (!animate) { el.textContent = String(to); return; }
      var obj = { v: 0 };
      el.textContent = "0";
      gsap.to(obj, {
        v: to, duration: 1, ease: "power2.out",
        onUpdate: function () { el.textContent = String(Math.round(obj.v)); },
        scrollTrigger: { trigger: el, start: "top 85%", once: true }
      });
    });
  }

  /* 空选择器上调 gsap.set 会在控制台刷 "GSAP target not found"。
     集合页只有收尾牌匾、没有 hero/材料/藏品室，所以统一走这个守卫。 */
  function setIf(sel, vars) {
    var els = $$(sel);
    if (els.length) gsap.set(els, vars);
  }

  /* --------------------------------------------------- 终态（reduced-motion）*/
  function settle() {
    setIf(".t3-hero__pool, .t3-hero__fig, .t3-hero__type .t3-label, .t3-hero .t3-hero__cta",
          { opacity: 1, y: 0, scale: 1 });
    setIf(".t3-line", { yPercent: 0, y: 0 });      /* y:0 的原因见 heroIntro() */
    setIf(".t3-hero__plate", { clipPath: CLIP_OPEN });
    setIf(".t3-spec__row", { clipPath: CLIP_OPEN });
    setIf(".t3-material__wash", { scaleX: 1, opacity: .3 });
    /* 视频在 reduced-motion 下根本不会拿到 src（见 heroVideo 的守卫），
       但前置态把它压成了 0 —— 不放出来 poster 也跟着没了，光缝会是一条死黑。 */
    setIf(".t3-hero__video", { opacity: .5 });
    /* 04 LIKENESS 落回「静态对切」：两张图各占一半，缝线在中间，一眼看懂 */
    // Native comparison retains its value across world/visibility/motion changes.
    setIf(".t3-closing__inner > *", { opacity: 1, y: 0 });
    counters(false);
  }

  /* ==========================================================================
     Hero 光缝里的视频（规范 §3.3 / §3.5）
     ========================================================================== */
  /* 三道闸门缺一不可，任何一道关上就只显示 poster（15KB 暗调静帧）：
       1) 桌面（≥900）—— 移动端连 <source> 都不插入，不是 display:none（§6.3）
       2) 非 reduced-motion
       3) 非省流量 / 非 2G
     再加一层 IntersectionObserver：进视口才 load()，离开就 pause。
     play() 的异常必须吞掉：iOS 低电量模式下 autoplay 一定失败（§9.4），
     那时 poster 就是最终画面，页面完全成立。 */
  function heroVideo() {
    var v = $("[data-t3-video]");
    if (!v) return;
    var mq = function (q) { return window.matchMedia(q).matches; };
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var thrifty = !!(conn && (conn.saveData || /(^|\W)[23]g\b|slow-2g/.test(conn.effectiveType || "")));
    if (!activeWorld() || !mq("(min-width: 900px)") || mq("(prefers-reduced-motion: reduce)") || thrifty) return;

    var webm = v.getAttribute("data-webm");
    var mp4 = v.getAttribute("data-mp4");
    if (!webm && !mp4) return;

    var loaded = !!v.querySelector("source");
    var visible = false;
    var observer = null;
    function load() {
      if (loaded) return;
      loaded = true;
      var src = "";
      if (webm) src += '<source src="' + webm + '" type="video/webm">';
      if (mp4) src += '<source src="' + mp4 + '" type="video/mp4">';
      v.insertAdjacentHTML("beforeend", src);
      v.preload = "auto";
      v.load();
    }
    function play() {
      if (!mounted || !activeWorld() || !visible || !mq("(min-width: 900px)") || mq("(prefers-reduced-motion: reduce)")) { v.pause(); return; }
      load(); var p = v.play();
      if (p && p.then) p.then(function () { if (!mounted || !activeWorld() || !visible) v.pause(); }).catch(function () {});
    }

    /* 光缝就在首屏，IO 会立刻命中 —— 所以要等 load 事件之后再挂，
       否则 400KB 的视频会和 LCP 的主角图抢带宽。 */
    function arm() {
      if (!mounted) return;
      if (!window.IntersectionObserver) return; // A poster is safer than an unobserved autoplay loop.
      observer = new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting;
        if (visible) play();
        else if (loaded) v.pause();
      }, { threshold: 0.05 });
      observer.observe(v);
    }
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    window.addEventListener("resize", play, { passive: true });
    cleanupTasks.push(function () {
      window.removeEventListener("load", arm);
      window.removeEventListener("resize", play);
      if (observer) observer.disconnect();
      v.pause();
      if (!activeWorld() || dataSaving() || mq("(prefers-reduced-motion: reduce)")) {
        $$("source", v).forEach(function (source) { source.remove(); });
        v.removeAttribute("src");
        if (loaded) v.load();
        loaded = false;
      }
    });
  }

  /* ==========================================================================
     05 · THE COMMISSION（规范 §2.5）—— 一条光从上往下把四步依次点亮
     ========================================================================== */
  function commission(animate) {
    var sec = $("[data-t3-commission]");
    if (!sec) return;
    var line = $("[data-t3-line]", sec);
    var steps = $$(".t3-step", sec);

    if (!animate) {
      if (line) gsap.set(line, { scaleY: 1 });
      steps.forEach(function (s) { s.classList.add("is-lit"); });
      return;
    }
    if (line) {
      gsap.fromTo(line, { scaleY: 0 }, {
        scaleY: 1, ease: "none",
        scrollTrigger: { trigger: sec, start: "top 70%", end: "bottom 80%", scrub: .5 }
      });
    }
    steps.forEach(function (s) {
      /* 圆点在光线经过时点亮。用 class 而不是 tween：颜色/glow 写在 CSS 的
         .is-lit 里，reduced-motion 分支直接加同一个 class 就是终态。 */
      ScrollTrigger.create({
        trigger: s, start: "top 78%", once: true,
        onEnter: function () { s.classList.add("is-lit"); }
      });
      /* immediateRender:false —— 内容默认在，滚到了才补淡入（Batch 2 的教训）*/
      gsap.from(s, {
        y: 16, opacity: 0, duration: .6, ease: "power3.out", immediateRender: false,
        scrollTrigger: { trigger: s, start: "top 84%", once: true }
      });
    });
  }

  /* ==========================================================================
     06 · THE COLLECTORS —— 印相纸在显影液里逐格浮现
     ========================================================================== */
  function collectors(animate) {
    var sheet = $("[data-t3-sheet]");
    if (!sheet) return;
    var cells = $$(".t3-sheet__cell", sheet);
    if (!cells.length || !animate) return;   /* 不动版 = 直接就是终态，无需 set */
    /* ⚠️ Batch 6 节奏修：原本是 ease:"none"（线性）。线性的淡入是"廉价感"最
       典型的来源 —— 显影不是匀速的，是先慢后快再定住。换 power2.out，
       时长同时从 .5 拉到 .62：格子多（12 格）时线性会让整块一起"啪"地出现。 */
    gsap.from(cells, {
      opacity: 0, duration: .62, ease: "power2.out", immediateRender: false,
      stagger: { each: .045, from: "random" },
      scrollTrigger: { trigger: sheet, start: "top 84%", once: true }
    });
  }


  /* ------------------------------------------------------------ 入场 timeline */
  /* 总长约 1.9s：光先亮 → 人浮起 → 巨字逐行上推 → 铜牌横向拉开 → CTA 落位。 */
  function heroIntro(full) {
    if (!hero || hero.getBoundingClientRect().bottom <= 0) return null;
    // Keep title and product visible while the short lift adds depth.
    var figure = hero.querySelector("[data-t3-fig]");
    var copy = hero.querySelectorAll(".t3-hero__type > *, .t3-hero__plate");
    var tl = gsap.timeline({ defaults: { overwrite: "auto", ease: "power3.out" } });
    if (figure) tl.fromTo(figure, { y: 18, scale: .98 }, { y: 0, scale: 1, duration: full ? .7 : .5 }, 0);
    if (copy.length) tl.fromTo(copy, { y: 10 }, { y: 0, duration: .5, stagger: .035 }, 0);
    return tl;
  }

  /* ==========================================================================
     03 · THE LIGHT ROOM（规范 §2.3）
     ========================================================================== */
  var room = $("[data-t3-room]");
  var rail = room && $("[data-t3-rail]", room);
  var track = room && $("[data-t3-track]", room);
  var bar = room && $("[data-t3-progress]", room);


  /* loading="lazy" 在「被 transform 横向平移、外面还套了一层 overflow:clip」的
     轨道里不可靠：实测 pin 到中段时第 4、5 张卡的图 naturalWidth 还是 0，
     卡是空的。章节快进视口时把这五张（合计约 250KB）改成 eager —— 首屏零成本，
     走到光室时图已经在手。 */
  function preloadRoomFigures() {
    if (!room || dataSaving()) return;
    var figs = $$(".t3-card__fig", room).filter(function (i) { return i.loading === "lazy"; });
    if (!figs.length) return;
    var go = function () { figs.forEach(function (i) { i.loading = "eager"; }); };
    if (!window.IntersectionObserver) return; // Native image lazy-loading remains the fallback.
    var io = new IntersectionObserver(function (es) {
      if (es[0].isIntersecting && activeWorld()) { go(); io.disconnect(); }
    }, { rootMargin: "600px 0px" });
    io.observe(room);
    cleanupTasks.push(function () { io.disconnect(); });
  }

  /* 未 pin（移动端 / reduced-motion / 桌面无 JS 升级）时轨道是原生可滚的，
     进度条跟着它自己的 scrollLeft 走 —— 横滚是非标准交互，进度条是它唯一的可见刻度。 */
  var nativeProgressBound = false;
  function roomProgressNative() {
    if (!track || !bar || nativeProgressBound) return;
    nativeProgressBound = true;
    var sync = function () {
      var max = track.scrollWidth - track.clientWidth;
      gsap.set(bar, { scaleX: max > 0 ? track.scrollLeft / max : 0 });
    };
    track.addEventListener("scroll", sync, { passive: true });
    sync();
    cleanupTasks.push(function () {
      track.removeEventListener("scroll", sync);
      nativeProgressBound = false;
    });
  }


  /* ------------------------------------------------------------ 收尾大牌匾 */
  function closing(animate) {
    var items = $$(".t3-closing__inner > *");
    if (!items.length) return;
    if (!animate) { gsap.set(items, { opacity: 1, y: 0 }); return; }
    /* ⚠️ immediateRender:false 是必须的。gsap.from 默认建好就把终点写成起点
       （opacity:0），然后等 trigger。收尾牌匾是全页最后一块，只要 trigger 因为
       任何原因没触发（页面被程序化整页截图、锚点直达底部、refresh 时坐标算错），
       它就永久隐身 —— 实测集合页整页截图时「MAKE THE ONLY ONE」和两颗 CTA
       整块是空的。改成不预渲染：内容默认就在，滚到了才补一次淡入。 */
    gsap.from(items, {
      y: 22, opacity: 0, duration: 0.7, stagger: 0.1, ease: "power3.out",
      immediateRender: false,
      scrollTrigger: { trigger: ".t3-closing", start: "top 82%", once: true }
    });
  }

  /* The world and entry lifecycle owns every observer, tween and video. */
  function mountWorldMotion() {
    if (mounted || !activeWorld()) return;
    mounted = true;
    doc.dataset.t3MotionState = dataSaving() ? "static-data-saving" : window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "static-reduced-motion" : "active";
    doc.classList.add("t3-anim-on");
    watchNavHeight();
    preloadRoomFigures();
    heroVideo();
    if (room) room.classList.remove("t3-room--pinned");
    motionContext = gsap.context(function () {
      settle();
      mediaContext = gsap.matchMedia();
      mediaContext.add("(prefers-reduced-motion: no-preference)", function () {
        if (dataSaving()) return;
        heroIntro(window.innerWidth >= 900);
        // Keep the gallery native at every width: no vertical-scroll pin.
        commission(true);
        collectors(true);
        closing(true);
      });
    });
    roomProgressNative();
    ScrollTrigger.refresh();
  }
  function unmountWorldMotion() {
    if (!mounted) return;
    mounted = false;
    cleanupTasks.splice(0).forEach(function (cleanup) { cleanup(); });
    if (mediaContext) { mediaContext.revert(); mediaContext = null; }
    if (motionContext) { motionContext.revert(); motionContext = null; }
    doc.classList.remove("t3-anim-on", "t3-js");
    if (room) room.classList.remove("t3-room--pinned");
    doc.dataset.t3MotionState = disposed ? "disposed" : suspended ? "suspended" : "inactive";
  }
  function syncWorldMotion() {
    lifecycleTick = 0;
    if (activeWorld()) mountWorldMotion();
    else { unmountWorldMotion(); doc.dataset.t3MotionState = disposed ? "disposed" : suspended ? "suspended" : "inactive"; }
  }
  function queueWorldMotion() {
    if (disposed || suspended) return;
    if (!activeWorld()) unmountWorldMotion();
    if (!lifecycleTick) lifecycleTick = window.requestAnimationFrame(syncWorldMotion);
  }
  ["vp:worldchange", "vp:entryready", "vp:transitionstart", "vp:transitionend"].forEach(function (eventName) {
    listen(window, eventName, queueWorldMotion);
  });
  listen(document, "visibilitychange", queueWorldMotion);
  function resetWorldMotion() { unmountWorldMotion(); queueWorldMotion(); }
  ["(prefers-reduced-motion: reduce)", "(min-width: 900px)"].forEach(function (query) {
    var condition = window.matchMedia(query);
    listen(condition, "change", resetWorldMotion);
  });
  listen(connection, "change", resetWorldMotion);
  function stopLifecycle() {
    window.cancelAnimationFrame(lifecycleTick);
    lifecycleTick = 0;
    unmountWorldMotion();
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    stopLifecycle();
    lifecycleCleanup.splice(0).forEach(function (cleanup) { cleanup(); });
    doc.dataset.t3MotionState = "disposed";
    delete window.__vpThreedMotionController;
  }
  listen(window, "pagehide", function (event) {
    if (!event.persisted) { dispose(); return; }
    suspended = true; stopLifecycle(); doc.dataset.t3MotionState = "suspended";
  });
  listen(window, "pageshow", function () { suspended = false; queueWorldMotion(); });
  if (window.MutationObserver) {
    var lifecycleObserver = new MutationObserver(queueWorldMotion);
    lifecycleObserver.observe(doc, { attributes: true, attributeFilter: ["data-world", "data-entry"] });
    lifecycleCleanup.push(function () { lifecycleObserver.disconnect(); });
  }
  window.__vpThreedMotionController = { dispose: dispose };
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () {
    if (mounted && activeWorld()) ScrollTrigger.refresh();
  });
  queueWorldMotion();
})();
